import { useSearchParams } from "react-router-dom";
import EmptyState from "../../../../components/empty-state";
import Download from "../../../../components/icons/download";
import Loading from "../../../../components/loading";
import { getDate } from "../../../../utils/formatDate";
import ClerkChartDisplay from "./clerk-charts-display";
import { useAuth } from "../../../../contexts/AuthContext";
import { ArrowPathIcon, BookmarkIcon, ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Listbox, Transition } from "@headlessui/react";
import { CheckIcon } from "@heroicons/react/20/solid";
import { Fragment } from 'react';
import { ChartListResponse, ChartStatusEnum } from '../../../../models/chart';

const ClerkChartsList = (props: any) => {
  const auth = useAuth();
  const clerkChartsState: any = props.state;
  const columnTitles = [
    "Precinct",
    "Township/City",
    "County",
    "Date",
    "Last Updated",
    "Status",
    "Actions",
  ];
  const status = props.status;
  const updateStatus = props.updateStatus;
  enum Statuses {
    "In Progress" = "In Progress",
    Completed = "Completed",
    Errored = "Errored",
    Other = "Other",
  }
  const downloadAll = props.downloadAll;;
  const duplicateChart = props.duplicateChart;
  const downloadChart = props.downloadChart;
  const markChartsAsPrinted = props.markChartsAsPrinted;
  const otherChartQuery = props.otherChartQuery;
  const setOtherChartQuery = props.setOtherChartQuery;

  return (
    <div className="flex flex-col w-full">
      <div className="flex items-center gap-4">
        <div className="relative">
          <Listbox
            value={clerkChartsState.otherChartStatus}
            onChange={(e) => {
              updateStatus(Statuses[e as keyof typeof Statuses]);
            }}
          >
            <div className="relative h-full mb-3 ml-12">
              <Listbox.Button className="flex items-center justify-between px-4 py-2 bg-white border rounded-md border-primary-blue min-w-64">
                <div>
                  Filter by Status:{" "}
                  <span className="pl-2 font-semibold text-primary-blue">
                    {Statuses[status as keyof typeof Statuses]}
                  </span>
                </div>
                <ChevronDownIcon className="w-4 h-4 ml-8" />
              </Listbox.Button>
              <Transition
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Listbox.Options className="absolute z-10 w-full py-1 mt-2 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                  {Object.keys(Statuses).map((status) => (
                    <Listbox.Option
                      key={`general-chart-status-${status}`}
                      className={({ active }) =>
                        `cursor-pointer select-none relative py-2 pl-10 pr-4 ${active
                          ? "text-blue-900 bg-blue-100"
                          : "text-gray-900"
                        }`
                      }
                      value={status}
                    >
                      {({ selected }) => (
                        <>
                          <span
                            className={`block truncate ${selected ? "font-medium" : "font-normal"
                              }`}
                          >
                            {Statuses[
                              status as keyof typeof Statuses
                            ]}
                          </span>
                          {selected ? (
                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                              <CheckIcon
                                className="w-5 h-5"
                                aria-hidden="true"
                              />
                            </span>
                          ) : null}
                        </>
                      )}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Transition>
            </div>
          </Listbox>
        </div>
        <div className='w-1/3'>
          <div className="container flex items-center gap-2 px-6 py-2 mx-auto mb-6 bg-white rounded-full">
            <MagnifyingGlassIcon className="w-8 h-8 text-gray-500" />
            <input
              key="search-input"
              className="w-full m-2 text-lg outline-none"
              placeholder="Search Charts by Municipality or Precinct"
              value={otherChartQuery}
              onChange={(e) => setOtherChartQuery(e.target.value)}
            />
          </div>
        </div>
      </div>
      {clerkChartsState.loading && <Loading />}
      {!clerkChartsState.loading && (
        <div className="flex flex-col w-full px-12 pb-36">
          {clerkChartsState?.length > 0 && (
            <div>
              {clerkChartsState.map((chart: any[], index: number) => {
                return (
                  <div
                    key={index}
                    className={`w-full ${index !== 0 ? "border-t mt-10 pt-10" : "pt-3 mb-8"
                      }`}
                  >
                    <div className="flex items-center w-full justify-items-stretch">
                      <div className="flex flex-row w-full mb-6">
                        <p className="justify-start w-full text-2xl font-semibold text-primary-blue">
                          {chart[0].county} - {getDate(chart[0].electionDate)}
                        </p>
                        <div className="flex flex-row justify-end w-full">
                          {chart
                            .map((elections, id) => {
                              if (elections.downloadLink) {
                                return true;
                              }
                            })
                            .includes(true) && (
                              <button
                                className="flex flex-row items-center text-base font-semibold cursor-pointer text-primary-blue"
                                onClick={() =>
                                  downloadAll(
                                    chart.map((c) => c.id),
                                    "charts"
                                  )
                                }
                              >
                                <Download className="w-6 mr-2" />
                                <p className="mt-2">Download All Charts</p>
                              </button>
                            )}
                          {auth.isAdmin && (
                            <button
                              className="flex flex-row items-center ml-8 text-base font-semibold cursor-pointer text-primary-blue"
                              onClick={() => {
                                chart.forEach((c) => {
                                  c.regenerateChart(c.id);
                                });
                              }
                              }
                            >
                              <ArrowPathIcon className="w-6 mr-2" />
                              <p className="mt-2">Regenerate All Charts</p>
                            </button>
                          )}
                          {clerkChartsState.filter((chart: ChartListResponse) => chart.status === ChartStatusEnum.Completed) > 0 &&
                            <div className='flex flex-row ml-10'>
                              <button
                                className='flex flex-row items-center text-base font-semibold cursor-pointer text-primary-blue'
                                onClick={() => markChartsAsPrinted(chart.map((c) => c.id))}
                              >
                                <BookmarkIcon
                                  className='w-6 mr-2'
                                />
                                <p
                                  className='mt-2'
                                >
                                  Mark Charts as Printed
                                </p>
                              </button>
                            </div>
                          }
                        </div>
                      </div>
                    </div>
                    <div className="grid w-full grid-cols-7 grid-rows-1 p-3 font-medium uppercase bg-white border font-poppins">
                      {columnTitles.map((title, _index) => (
                        <p key={_index}>{title}</p>
                      ))}
                    </div>
                    {chart.map((c, i: number) => {
                      return (
                        <div key={`chart-${c.id}`}>
                          <ClerkChartDisplay
                            chart={c}
                            getDate={getDate}
                            deleteChart={c.deleteChart}
                            downloadChart={downloadChart}
                            duplicateChart={duplicateChart}
                            regenerateChart={c.regenerateChart}
                            markChartsAsPrinted={markChartsAsPrinted}
                            backgroundColor={
                              i % 2 === 0
                                ? "bg-secondary-lightblue"
                                : "bg-white"
                            }
                          />
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
          {clerkChartsState.length === 0 &&
            !clerkChartsState.debouncedQuery && (
              <div className="w-full col-span-3">
                <EmptyState>No Charts Found</EmptyState>
              </div>
            )}
        </div>
      )}
    </div>
  );
};

export default ClerkChartsList;
